import { Check } from "@untitledui/icons";
import { memo, type ReactNode, useCallback } from "react";
import { UnavailableOption } from "@/components/filters/shared/unavailable-option";
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { cx } from "@/utils/cx";
import { GROUP_ICONS } from "./risk-groups";

export type RiskOption = { key: string; name: string };
export type RiskOptionGroup<TItem extends RiskOption> = { id: string; name: string; items: TItem[] };

export const filterRiskOptionGroups = <TItem extends RiskOption>(
  groups: RiskOptionGroup<TItem>[],
  search: string,
): RiskOptionGroup<TItem>[] => {
  const query = search.trim().toLowerCase();
  if (!query) return groups;
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.name.toLowerCase().includes(query)) }))
    .filter((group) => group.items.length > 0);
};

type RiskOptionItemProps<TItem extends RiskOption> = {
  item: TItem;
  groupId: string;
  selected: boolean;
  disabled?: boolean;
  indicator?: ReactNode;
  unavailableReason?: string;
  onSelect: (item: TItem) => void;
};

const RiskOptionItemInner = <TItem extends RiskOption>({
  item,
  groupId,
  selected,
  disabled,
  unavailableReason,
  indicator,
  onSelect,
}: RiskOptionItemProps<TItem>) => {
  const Icon = GROUP_ICONS[groupId];
  const leading = indicator ?? (Icon && <Icon aria-hidden="true" className="size-5 shrink-0 text-fg-quaternary" />);
  const handleSelect = useCallback(() => onSelect(item), [onSelect, item]);

  if (unavailableReason && !selected) {
    return (
      <CommandItem value={item.key} disabled className="p-0 data-[disabled=true]:pointer-events-auto">
        <UnavailableOption
          semantics="inherit"
          reason={unavailableReason}
          className="flex flex-1 items-center gap-2 px-2 py-1.5"
        >
          {leading}
          <span className="flex-1 truncate">{item.name}</span>
        </UnavailableOption>
      </CommandItem>
    );
  }

  return (
    <CommandItem value={item.key} onSelect={handleSelect} disabled={disabled} className="gap-2">
      {leading}
      <span className="flex-1 truncate">{item.name}</span>
      <Check aria-hidden="true" className={cx("size-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
    </CommandItem>
  );
};

const RiskOptionItem = memo(RiskOptionItemInner) as typeof RiskOptionItemInner;

type RiskOptionListProps<TItem extends RiskOption> = {
  groups: RiskOptionGroup<TItem>[];
  isLoading?: boolean;
  isSelected: (item: TItem) => boolean;
  isDisabled?: (item: TItem) => boolean;
  getUnavailableReason?: (item: TItem) => string | undefined;
  /** Shown in place of the group icon, e.g. whether the risk is monitored. */
  getIndicator?: (item: TItem) => ReactNode;
  onSelect: (item: TItem) => void;
};

export const RiskOptionList = <TItem extends RiskOption>({
  groups,
  isLoading,
  isSelected,
  isDisabled,
  getUnavailableReason,
  getIndicator,
  onSelect,
}: RiskOptionListProps<TItem>) => (
  <CommandList>
    <CommandEmpty>{isLoading ? "Loading..." : "No risks found"}</CommandEmpty>
    {groups.map((group, index) => (
      <CommandGroup
        key={group.id}
        heading={group.name || undefined}
        className={cx(index < groups.length - 1 && "mb-2 border-b border-secondary pb-2")}
      >
        {group.items.map((item) => (
          <RiskOptionItem
            key={item.key}
            item={item}
            groupId={group.id}
            selected={isSelected(item)}
            disabled={isDisabled?.(item)}
            unavailableReason={getUnavailableReason?.(item)}
            indicator={getIndicator?.(item)}
            onSelect={onSelect}
          />
        ))}
      </CommandGroup>
    ))}
  </CommandList>
);
